import React, { useState, useEffect } from 'react';
import {
  Building2, Users, Sparkles, Heart, Map, Inbox, Plug,
  ArrowLeft, ArrowRight, Search, MapPin, TrendingUp, Activity,
  Briefcase, MessageSquare, Calendar, BarChart3, ChevronRight,
  HelpCircle, Phone, RefreshCw, Mic, Menu, X, Megaphone
} from 'lucide-react';
import DeoLogo from './DeoLogo';
import DeoBot, { DeoBotPlaque } from './DeoBot';
import { properties } from '../data/properties';
import {
  PortfolioListPage, CustomersPage, SmartMatchingPage,
  FavoritesPage, MapPage, RequestPoolPage, IntegrationsPage,
  CRMPage, AdsPanelPage,
} from './SubPages';

/* Common: Light bej-gradient page background (referans: bej gradyan derinlikli) */
const pageBg = {
  background: `
    radial-gradient(ellipse 80% 60% at 20% 0%, rgba(244, 213, 184, 0.7), transparent 60%),
    radial-gradient(ellipse 60% 50% at 90% 100%, rgba(232, 184, 148, 0.55), transparent 65%),
    radial-gradient(ellipse 100% 100% at 50% 50%, #F4ECDF 0%, #E8DECD 50%, #D8CAB1 100%)
  `,
};
const woodGrain = {
  backgroundImage: `
    repeating-linear-gradient(90deg,
      transparent 0px, rgba(140, 90, 50, 0.05) 1px, transparent 3px,
      transparent 60px, rgba(180, 130, 80, 0.03) 61px, transparent 63px,
      transparent 130px, rgba(100, 60, 30, 0.04) 131px, transparent 133px
    )
  `,
  opacity: 0.7,
};

/* Hero (kilit panel) ekranı: koyu kahverengi/espresso, opener ile uyumlu */
const heroBg = {
  background: `
    radial-gradient(ellipse 60% 40% at 50% 0%, rgba(224,138,79,0.18), transparent 60%),
    radial-gradient(ellipse 70% 60% at 50% 50%, #4A2F20 0%, #2B1E16 45%, #160C07 100%)
  `,
};
const heroWoodGrain = {
  backgroundImage: `
    repeating-linear-gradient(90deg,
      transparent 0px,
      rgba(70,40,20,0.18) 1px,
      transparent 3px,
      transparent 28px,
      rgba(140,80,40,0.06) 29px,
      transparent 31px,
      transparent 80px,
      rgba(40,20,10,0.22) 81px,
      transparent 84px,
      transparent 140px,
      rgba(180,110,60,0.08) 141px,
      transparent 143px
    )
  `,
  opacity: 0.85,
};

const navItems = [
  { icon: BarChart3, label: 'Genel Bakış', key: 'overview' },
  { icon: Building2, label: 'Portföy Listesi', key: 'portfolio' },
  { icon: Users, label: 'Müşteriler', key: 'customers' },
  { icon: Sparkles, label: 'Akıllı Eşleştirme', key: 'matching' },
  { icon: Heart, label: 'Favoriler', key: 'favorites' },
  { icon: Map, label: 'Harita', key: 'map' },
  { icon: Inbox, label: 'Talep Havuzu', key: 'requests' },
  { icon: RefreshCw, label: 'CRM', key: 'crm' },
  { icon: Megaphone, label: 'Reklam Paneli', key: 'ads' },
  { icon: Plug, label: 'Entegrasyonlar', key: 'integrations' },
];

const quickModules = [
  { icon: RefreshCw, label: 'CRM', desc: 'Tek panelden müşteri akışı' },
  { icon: Sparkles, label: 'Eşleştirme', desc: 'AI portföy-müşteri eşleştirme' },
  { icon: Inbox, label: 'Talep Havuzu', desc: 'Açık talepler ve tetikler' },
  { icon: HelpCircle, label: 'SSS', desc: 'Hızlı yardım merkezi' },
  { icon: Phone, label: 'İletişim', desc: 'Müşteri & takım iletişim' },
];

const antalyaDistricts = [
  { name: 'Konyaaltı',  count: 64, change: '+12%', value: '₺ 1.2B', trend: 'up' },
  { name: 'Lara',       count: 52, change: '+8%',  value: '₺ 980M', trend: 'up' },
  { name: 'Kalkan',     count: 38, change: '+22%', value: '₺ 1.6B', trend: 'up' },
  { name: 'Belek',      count: 29, change: '+5%',  value: '₺ 540M', trend: 'up' },
  { name: 'Kaş',        count: 24, change: '-3%',  value: '₺ 420M', trend: 'down' },
  { name: 'Side',       count: 19, change: '+15%', value: '₺ 360M', trend: 'up' },
];

const recentActivity = [
  { time: '14:32', type: 'match', text: 'Konyaaltı 4+1 → Mehmet Y. ile eşleşti', tone: 'mint' },
  { time: '13:18', type: 'view',  text: 'Lara penthouse · 12 yeni görüntüleme', tone: 'copper' },
  { time: '12:04', type: 'sale',  text: 'Kalkan villa rezerve edildi (DEO-2403)', tone: 'yellow' },
  { time: '11:42', type: 'lead',  text: '8 yeni müşteri talebi düştü', tone: 'mint' },
  { time: '10:21', type: 'note',  text: 'Side rezidans için sunum oluşturuldu', tone: 'copper' },
];

export default function Dashboard() {
  const [view, setView] = useState('hero'); // 'hero' = sade açılış, 'panel' = full dashboard
  const [activeNav, setActiveNav] = useState('Genel Bakış');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('yonetici'); // yonetici | danisman

  const today = new Date().toLocaleDateString('tr-TR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      data-testid="dashboard-page"
      style={view === 'hero' ? heroBg : pageBg}
    >
      <div className="absolute inset-0 pointer-events-none" style={view === 'hero' ? heroWoodGrain : woodGrain} />

      {/* ============ LEFT GLASS SIDEBAR — hidden in hero/waiting view ============ */}
      {view === 'panel' && (
      <aside
        className="hidden sm:flex fixed left-0 top-0 h-screen z-30 flex-col items-center py-6"
        style={{
          width: '88px',
          background: 'linear-gradient(180deg, #2B1E16 0%, #1C130D 100%)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRight: '1px solid rgba(197, 108, 57, 0.40)',
          boxShadow: '4px 0 32px -8px rgba(0,0,0,0.55)',
        }}
        data-testid="left-sidebar"
      >
        <button
          className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all hover:bg-[#E08A4F]/15"
          style={{ background: 'rgba(244,213,184,0.06)', border: '1px solid rgba(197,108,57,0.30)' }}
          data-testid="back-button"
          onClick={() => setView(view === 'panel' ? 'hero' : 'panel')}
          aria-label="Görünümü değiştir"
        >
          {view === 'panel' ? <ArrowLeft size={18} className="text-[#E08A4F]" /> : <ArrowRight size={18} className="text-[#E08A4F]" />}
        </button>

        <div className="w-8 h-px my-6" style={{ background: 'linear-gradient(90deg, transparent, rgba(224,138,79,0.55), transparent)' }} />

        <nav className="flex flex-col gap-2 flex-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = item.label === activeNav;
            return (
              <button
                key={item.key}
                onClick={() => { setActiveNav(item.label); setView('panel'); }}
                className="relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group"
                style={{
                  background: isActive ? 'linear-gradient(135deg, #E08A4F, #C56C39)' : 'transparent',
                  border: isActive ? '1px solid rgba(244,213,184,0.5)' : '1px solid transparent',
                  boxShadow: isActive ? '0 1px 0 rgba(244,213,184,0.5) inset, 0 4px 12px -4px rgba(197,108,57,0.55)' : 'none',
                }}
                data-testid={`sidebar-${item.key}`}
                aria-label={item.label}
              >
                <Icon size={18} className={isActive ? 'text-[#1C130D]' : 'text-[#E08A4F] group-hover:text-[#F4D5B8]'} />
                <span className="absolute left-full ml-3 px-2.5 py-1 rounded-md text-[11px] font-body tracking-wider uppercase whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-50"
                  style={{ background: 'rgba(15,8,4,0.95)', border: '1px solid rgba(197,108,57,0.4)', color: '#E08A4F' }}
                >
                  {item.label}
                </span>
                {isActive && (
                  <span className="absolute -right-3 top-1/2 -translate-y-1/2 w-1 h-6 rounded-full"
                    style={{ background: 'linear-gradient(180deg, #E08A4F, #C56C39)' }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <button
          className="w-12 h-12 rounded-2xl flex items-center justify-center hover:bg-[#E08A4F]/15 transition-all relative"
          style={{ background: 'rgba(244,213,184,0.06)', border: '1px solid rgba(197,108,57,0.30)' }}
          data-testid="sidebar-voice"
          aria-label="Sesli arama"
        >
          <Mic size={16} className="text-[#E08A4F]" />
          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#68E5B2]" />
        </button>
      </aside>
      )}

      {/* ============ MOBILE TOP BAR — sm:hidden ============ */}
      {view === 'panel' && (
        <MobileTopBar
          navItems={navItems}
          activeNav={activeNav}
          setActiveNav={setActiveNav}
          onBack={() => setView('hero')}
        />
      )}

      {/* ============ MAIN CONTENT ============ */}
      <main className={`relative z-10 ${view === 'panel' ? 'sm:ml-[88px] pt-14 sm:pt-0' : ''}`}>
        {view === 'hero' ? (
          <HeroView role={role} setRole={setRole} onEnter={() => setView('panel')} />
        ) : (
          <PanelView
            activeNav={activeNav}
            today={today}
            search={search}
            setSearch={setSearch}
            role={role}
          />
        )}
      </main>

      {/* Fixed metallic plaque — sayfa sağ alt */}
      <DeoBotPlaque />
    </div>
  );
}

/* ============ HERO VIEW (sade açılış) ============ */
function HeroView({ onEnter, role, setRole }) {

  return (
    <section className="min-h-screen flex flex-col lg:flex-row items-center justify-center px-4 sm:px-8 lg:px-12 py-8 lg:py-12 relative gap-8" data-testid="hero-view">
      <div className="flex-1 flex justify-center w-full">
        <div
          className="relative px-6 sm:px-10 py-8 sm:py-10 flex flex-col items-center w-full"
          style={{
            maxWidth: 'min(380px, 92vw)',
            minHeight: 'min(600px, 75vh)',
            borderRadius: 'clamp(120px, 32vw, 180px)',
            background: 'radial-gradient(ellipse at 50% 30%, rgba(197,108,57,0.10) 0%, rgba(28,19,13,0.95) 50%, #0B0703 100%)',
            border: '1px solid rgba(197, 108, 57, 0.45)',
            boxShadow: 'inset 0 0 60px rgba(0,0,0,0.7), 0 24px 60px -12px rgba(0,0,0,0.7)',
          }}
        >
          <div className="absolute inset-2 rounded-[176px] pointer-events-none"
            style={{ border: '1px solid rgba(197,108,57,0.18)' }} />

          <div className="relative z-10 flex flex-col items-center pt-4">
            <DeoLogo size={170} tone="bronze" />

            {/* Sadece D E O — boşluklu */}
            <div className="mt-7 text-center">
              <div className="font-display copper-shine font-semibold tracking-[0.65em] text-[20px] uppercase pl-[0.65em]">
                D E O
              </div>
            </div>

            <div className="divider-copper my-5 w-24" />

            <div className="font-display copper-text font-light tracking-[0.12em] text-[15px] text-center px-4">
              Emlak Ofisinizin
            </div>
            <div className="font-display copper-shine font-semibold tracking-[0.06em] text-[19px] text-center mt-1">
              Dijital Zekâsı
            </div>

            {/* Rol seçici sekmesi */}
            <div
              className="mt-7 rounded-full p-1 flex gap-1"
              style={{
                background: 'rgba(15,8,4,0.85)',
                border: '1px solid rgba(197,108,57,0.35)',
                boxShadow: 'inset 0 1px 0 rgba(244,213,184,0.08)',
              }}
              data-testid="role-tabs"
            >
              {[
                { key: 'yonetici', label: 'Yönetici' },
                { key: 'danisman', label: 'Danışman' },
              ].map(r => (
                <button
                  key={r.key}
                  onClick={() => setRole(r.key)}
                  className="px-4 py-1.5 rounded-full font-display text-[11px] font-medium tracking-[0.16em] uppercase transition-all"
                  style={{
                    background: role === r.key
                      ? 'linear-gradient(135deg, #E08A4F 0%, #C56C39 50%, #8E4A23 100%)'
                      : 'transparent',
                    color: role === r.key ? '#1C130D' : '#F4ECDF',
                    boxShadow: role === r.key ? '0 1px 0 rgba(244,213,184,0.5) inset, 0 4px 10px -4px rgba(197,108,57,0.5)' : 'none',
                  }}
                  data-testid={`role-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div className="font-body text-[8.5px] text-[#F4ECDF]/40 tracking-[0.24em] uppercase mt-3 text-center">
              {role === 'yonetici' ? 'Tüm Ofis · Tüm Acentalar · Raporlar' : 'Kişisel Portföy · Müşterilerim · Eşleştirme'}
            </div>

            <button
              onClick={onEnter}
              className="mt-7 px-7 py-3 rounded-full flex items-center gap-2.5 group transition-all hover:scale-[1.03]"
              style={{
                background: 'linear-gradient(135deg, #E08A4F 0%, #C56C39 50%, #8E4A23 100%)',
                border: '1px solid rgba(244,213,184,0.4)',
                boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset, 0 8px 20px -4px rgba(197,108,57,0.5)',
              }}
              data-testid="hero-enter-button"
            >
              <span className="font-display text-[12px] tracking-[0.22em] text-[#1C130D] font-semibold uppercase">
                Panele Geç
              </span>
              <ChevronRight size={14} className="text-[#1C130D] group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Hero left top - Bölümler reklam tanıtım bloğu */}
      <div className="absolute left-12 top-12 hidden xl:block max-w-[300px]" data-testid="hero-section-chips">
        <div className="font-display text-[10px] text-[#E08A4F]/80 tracking-[0.32em] uppercase mb-3 font-semibold">Bölümler</div>
        <h2 className="font-display text-[22px] leading-[1.2] font-light text-[#F4ECDF] tracking-tight mb-5">
          Sekiz modül,
          <span className="copper-shine font-semibold italic"> tek panel.</span>
        </h2>
        <div className="space-y-2.5">
          {[
            { num: '01', label: 'Genel Bakış' },
            { num: '02', label: 'Vitrin' },
            { num: '03', label: 'Müşteriler' },
            { num: '04', label: 'Akıllı Eşleştirme' },
            { num: '05', label: 'Favoriler' },
            { num: '06', label: 'Harita' },
            { num: '07', label: 'Talep Havuzu' },
            { num: '08', label: 'Entegrasyonlar' },
          ].map((s) => (
            <div key={s.num} className="flex items-center gap-3">
              <span className="font-body text-[11px] copper-text font-bold w-6">{s.num}</span>
              <span className="font-display text-[14px] text-[#F4ECDF]/75 tracking-tight">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* DEO Bot - desktop sağ üst, mobilde keyhole altında stack */}
      <div className="lg:absolute lg:top-10 lg:right-12 z-20 flex justify-center w-full lg:w-auto mt-4 lg:mt-0">
        <DeoBot sphereBoost={1.55} />
      </div>
    </section>
  );
}

/* ============ PANEL VIEW (full dashboard) ============ */
function PanelView({ activeNav, today, search, setSearch, role }) {
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 240);
    const onResize = () => setIsMobile(window.innerWidth < 640);
    onResize();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const showSticky = scrolled || isMobile;
  const showInline = !showSticky;

  /* Sub-page render based on active nav */
  const renderContent = () => {
    switch (activeNav) {
      case 'Portföy Listesi': return <PortfolioListPage role={role} />;
      case 'Müşteriler': return <CustomersPage role={role} />;
      case 'Akıllı Eşleştirme': return <SmartMatchingPage role={role} />;
      case 'Favoriler': return <FavoritesPage />;
      case 'Harita': return <MapPage />;
      case 'Talep Havuzu': return <RequestPoolPage />;
      case 'Entegrasyonlar': return <IntegrationsPage />;
      case 'CRM': return <CRMPage />;
      case 'Reklam Paneli': return <AdsPanelPage />;
      default: return null;
    }
  };

  if (activeNav !== 'Genel Bakış') {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-32 relative" data-testid="panel-view-subpage">
        <header className="flex flex-col lg:flex-row items-start justify-between mb-6 gap-4 lg:gap-8 relative z-10">
          <div className="max-w-[480px]">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="font-display text-[11px] tracking-[0.18em] text-[#8E4A23] font-semibold uppercase">
                {role === 'yonetici' ? 'Yönetici Modu' : 'Danışman Modu'}
              </div>
              <div className="w-1 h-1 rounded-full bg-[#8E4A23]/50" />
              <div className="font-display text-[11px] tracking-[0.18em] text-[#5A2F12]/60 uppercase">
                {today}
              </div>
            </div>
            <h1 className="font-display text-[22px] sm:text-[26px] leading-[1.1] font-light text-[#1C130D] tracking-tight">
              <span className="copper-shine font-semibold">{activeNav}</span>
            </h1>
          </div>
          {showInline && (
            <div className="flex-shrink-0 relative z-20 self-end lg:self-auto">
              <DeoBot compact inner />
            </div>
          )}
        </header>
        {renderContent()}
        {showSticky && <DeoBot sticky />}
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-32 relative" data-testid="panel-view">
      {showSticky && <DeoBot sticky />}
      {/* Blurred brand watermark - top right corner (referansa göre cam arkası blur) */}
      <div
        className="absolute top-0 right-0 pointer-events-none z-0 overflow-hidden"
        style={{
          width: '420px',
          height: '420px',
          opacity: 0.18,
          filter: 'blur(2px)',
          maskImage: 'radial-gradient(ellipse at top right, black 30%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at top right, black 30%, transparent 70%)',
        }}
        data-testid="brand-watermark"
      >
        <div
          className="absolute -top-12 -right-16 px-12 py-10 flex flex-col items-center"
          style={{
            width: '280px',
            minHeight: '420px',
            borderRadius: '140px',
            background: 'radial-gradient(ellipse at 50% 30%, rgba(197,108,57,0.10) 0%, rgba(28,19,13,0.6) 50%, rgba(11,7,3,0.4) 100%)',
            border: '1px solid rgba(197, 108, 57, 0.35)',
            transform: 'rotate(8deg)',
          }}
        >
          <DeoLogo size={90} />
        </div>
      </div>

      {/* Top bar - date + section + small brand */}
      <header className="flex flex-col lg:flex-row items-start justify-between mb-8 lg:mb-10 gap-4 lg:gap-8 relative z-10">
        <div className="max-w-[560px]">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="font-display text-[11px] tracking-[0.18em] text-[#8E4A23] font-semibold uppercase">
              {activeNav}
            </div>
            <div className="w-1 h-1 rounded-full bg-[#8E4A23]/50" />
            <div className="font-display text-[11px] tracking-[0.18em] text-[#5A2F12]/60 uppercase">
              247 aktif · 4.8B portföy değeri
            </div>
          </div>
          <h1 className="font-display text-[28px] sm:text-[36px] lg:text-[40px] leading-[1.1] font-light text-[#1C130D] tracking-tight">
            Selam <span className="copper-shine font-semibold">Mehmet K.,</span>
            <br/>
            <span className="font-light text-[#3A2A1F]">Hadi biraz </span>
            <span className="copper-shine font-semibold italic">kazanalım..</span>
          </h1>

          {/* Tarih bloğu - sade eyebrow */}
          <div className="mt-6 pl-4" style={{ borderLeft: '2px solid rgba(197,108,57,0.65)' }} data-testid="deo-date-block">
            <div className="font-display text-[11px] tracking-[0.18em] text-[#8E4A23] font-semibold uppercase mb-1.5">
              Tarih
            </div>
            <div className="font-body text-[14px] text-[#1C130D]/85 capitalize">
              {today}
            </div>
          </div>

          {/* DEO system description - aynı sade eyebrow */}
          <div className="mt-6 pl-4" style={{ borderLeft: '2px solid rgba(197,108,57,0.65)' }} data-testid="deo-system-description">
            <div className="font-display text-[11px] tracking-[0.18em] text-[#8E4A23] font-semibold uppercase mb-1.5">
              Sistem Tanımı
            </div>
            <p className="font-body text-[14px] leading-[1.65] text-[#1C130D]/85">
              <span className="copper-shine font-semibold">DEO;</span> sesli arama, akıllı eşleştirme,
              sunum üretimi ve harita merkezini tek bir sade panelde birleştirir.
              Klavyeye dokunmadan <span className="copper-shine font-semibold">23.335 müşteri</span> ve
              <span className="copper-shine font-semibold"> 423 aktif portföyü</span> yönetin.
            </p>
          </div>
        </div>

        {/* Top-right: DEO Bot (yatay düzen, plaka yok) — scroll'da ve mobilde sticky'e geçer */}
        {showInline && (
          <div className="flex-shrink-0 relative z-20 self-end lg:self-auto">
            <DeoBot inner />
          </div>
        )}
      </header>

      {/* Blurred small brand panel - top-right corner accent */}
      <div className="absolute top-6 right-[300px] pointer-events-none opacity-50" style={{
        filter: 'blur(1px)',
      }} data-testid="brand-blur-corner">
        {/* Hidden - actually rendered above as watermark */}
      </div>

      {/* Section divider: Genel Bakış */}
      <div className="flex items-center gap-3 mb-5 mt-2 relative z-10">
        <div className="font-body text-[9px] tracking-[0.32em] text-[#8E4A23] font-semibold uppercase">
          01 · Genel Bakış
        </div>
        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(142,74,35,0.5), transparent)' }} />
      </div>

      {/* Stats row + smart search */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 mb-8 lg:mb-10" data-testid="stats-row">
        {/* Performans Özeti — kahve zemin */}
        <div
          className="lg:col-span-7 rounded-3xl p-5 sm:p-6 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #2B1E16 0%, #1C130D 100%)',
            border: '1px solid rgba(197,108,57,0.40)',
            boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 24px 48px -12px rgba(0,0,0,0.55)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="font-body text-[10px] tracking-[0.28em] text-[#E08A4F] font-semibold uppercase">
              Performans Özeti
            </div>
            <div className="capsule" style={{ background: 'rgba(244,213,184,0.10)', color: '#E08A4F', border: '1px solid rgba(224,138,79,0.45)' }}>
              <TrendingUp size={10} /> Bu Ay
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            <StatCell value="23.335" label="Toplam Müşteri" delta="+1.842" dark />
            <StatCell value="423" label="Açık Portföy" delta="+38" dark />
            <StatCell value="127" label="Açılan (bu ay)" delta="+22%" dark />
          </div>
        </div>

        {/* Smart Search */}
        <div
          className="lg:col-span-5 rounded-3xl p-2 flex items-center gap-2"
          style={{
            background: 'linear-gradient(135deg, rgba(28,19,13,0.06), rgba(28,19,13,0.02))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(142,74,35,0.30)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.30) inset',
          }}
        >
          <div className="pl-4 flex-shrink-0">
            <Search size={18} className="text-[#E08A4F]" />
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Portföy, müşteri, ilçe ara..."
            className="flex-1 bg-transparent border-0 outline-none text-[#1C130D] placeholder:text-[#5A2F12]/50 px-2 py-3 font-body text-[14px]"
            data-testid="panel-search"
          />
          <button className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #E08A4F 0%, #C56C39 50%, #8E4A23 100%)',
              boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset',
            }}
            data-testid="panel-search-button"
          >
            <Mic size={15} className="text-[#1C130D]" />
          </button>
        </div>
      </section>

      {/* Section divider: Bölge Analizi */}
      <div className="flex items-center gap-3 mb-5 relative z-10">
        <div className="font-body text-[9px] tracking-[0.32em] text-[#8E4A23] font-semibold uppercase">
          03 · Bölge Analizi & Canlı Akış
        </div>
        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(142,74,35,0.5), transparent)' }} />
      </div>

      {/* Antalya Analytics + Recent Activity row */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 mb-10">
        <div
          className="lg:col-span-7 rounded-3xl p-5 sm:p-6"
          style={{
            background: 'linear-gradient(135deg, #2B1E16 0%, #1C130D 100%)',
            border: '1px solid rgba(197,108,57,0.40)',
            boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 24px 48px -12px rgba(0,0,0,0.55)',
          }}
          data-testid="antalya-analytics"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-body text-[10px] tracking-[0.28em] text-[#E08A4F] font-semibold uppercase mb-1.5">
                İlçe Bazlı Analiz
              </div>
              <h3 className="font-display text-[22px] text-[#F4ECDF] font-light tracking-tight">
                Antalya · <span className="copper-shine font-medium">Bölge Performansı</span>
              </h3>
            </div>
            <button className="capsule capsule-copper hover:bg-[#C56C39]/20 transition-colors">
              <Map size={11}/> Harita
            </button>
          </div>

          <div className="space-y-2.5">
            {antalyaDistricts.map((d, i) => (
              <div key={d.name} className="grid grid-cols-12 gap-3 items-center py-2.5 border-b border-white/5 last:border-0"
                data-testid={`district-${d.name.toLowerCase()}`}
              >
                <div className="col-span-1 font-body text-[10px] text-[#E08A4F]/70 font-semibold">{String(i+1).padStart(2,'0')}</div>
                <div className="col-span-3 font-display text-[14px] text-[#F4ECDF] font-medium">{d.name}</div>
                <div className="col-span-3">
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(244,213,184,0.10)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(d.count/64)*100}%`,
                        background: 'linear-gradient(90deg, #E08A4F, #C56C39)',
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-2 font-body text-[12px] text-[#F4ECDF]/65">{d.count} ilan</div>
                <div className="col-span-2 font-display text-[14px] copper-shine font-bold">{d.value}</div>
                <div className={`col-span-1 font-body text-[10px] text-right font-semibold ${d.trend === 'up' ? 'text-[#68E5B2]' : 'text-[#F7D002]'}`}>
                  {d.change}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live activity feed */}
        <div
          className="lg:col-span-5 rounded-3xl p-5 sm:p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(15,8,4,0.92), rgba(28,19,13,0.85))',
            backdropFilter: 'blur(14px)',
            border: '1px solid rgba(197, 108, 57, 0.40)',
            boxShadow: '0 1px 0 rgba(244,213,184,0.10) inset, 0 16px 40px -12px rgba(0,0,0,0.5)',
          }}
          data-testid="recent-activity"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-body text-[9px] tracking-[0.32em] text-[#68E5B2] font-semibold uppercase mb-1.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#68E5B2] animate-pulse" />
                Canlı
              </div>
              <h3 className="font-display text-[22px] text-[#F4ECDF] font-light tracking-tight">
                Son <span className="copper-shine font-medium">Hareketler</span>
              </h3>
            </div>
          </div>

          <div className="space-y-3.5">
            {recentActivity.map((a, i) => (
              <div key={i} className="flex items-start gap-3 pb-3.5 border-b border-white/5 last:border-0"
                data-testid={`activity-${i}`}
              >
                <div className="font-body text-[10px] text-[#F4ECDF]/40 pt-1 w-10 flex-shrink-0">{a.time}</div>
                <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                  a.tone === 'mint' ? 'bg-[#68E5B2]' : a.tone === 'yellow' ? 'bg-[#F7D002]' : 'bg-[#E08A4F]'
                }`} />
                <div className="flex-1 font-body text-[12.5px] text-[#F4ECDF]/85 leading-snug">
                  {a.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section divider: Vitrin */}
      <div className="flex items-center gap-3 mb-5 relative z-10">
        <div className="font-body text-[9px] tracking-[0.32em] text-[#8E4A23] font-semibold uppercase">
          02 · Vitrin
        </div>
        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(142,74,35,0.5), transparent)' }} />
      </div>

      {/* Portfolio cards */}
      <section className="mb-10" data-testid="portfolio-section">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="font-body text-[9px] tracking-[0.32em] text-[#8E4A23] font-semibold uppercase mb-1.5">
              Premium Vitrin
            </div>
            <h3 className="font-display text-[24px] text-[#1C130D] font-light tracking-tight">
              Öne Çıkan <span className="copper-shine font-medium">Portföy</span>
            </h3>
          </div>
          <button className="capsule capsule-copper">Tümünü Gör <ChevronRight size={11}/></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {properties.slice(0, 3).map(p => (
            <div
              key={p.id}
              className="rounded-2xl overflow-hidden property-card"
              style={{
                background: 'linear-gradient(135deg, rgba(15,8,4,0.92), rgba(28,19,13,0.85))',
                border: '1px solid rgba(197, 108, 57, 0.40)',
                backdropFilter: 'blur(14px)',
                boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 16px 40px -12px rgba(0,0,0,0.55)',
              }}
              data-testid={`property-${p.id}`}
            >
              <div className="relative h-44 overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0703] via-transparent to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="capsule capsule-copper backdrop-blur-md">{p.type}</span>
                </div>
                <div className="absolute bottom-3 left-4 font-display text-[20px] copper-shine font-semibold">
                  {p.price}
                </div>
              </div>

              <div className="p-5">
                <h4 className="font-display text-[15px] font-medium text-[#F4ECDF] leading-tight mb-1.5">
                  {p.title}
                </h4>
                <div className="flex items-center gap-1 text-[#F4ECDF]/45 text-[10px] mb-4 font-body tracking-wider uppercase">
                  <MapPin size={10} /> {p.location}
                </div>

                {/* 3 highlight capsules — readable on dark */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {p.highlights.map((h, i) => (
                    <span key={i} className={`capsule ${i === 0 ? 'capsule-mint' : i === 1 ? 'capsule-yellow' : 'capsule-copper'}`}>
                      {h}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/8 text-center">
                  <div>
                    <div className="font-body text-[9px] text-white/40 uppercase tracking-wider">m²</div>
                    <div className="font-display text-[13px] text-[#F4ECDF] font-medium mt-0.5">{p.size}</div>
                  </div>
                  <div>
                    <div className="font-body text-[9px] text-white/40 uppercase tracking-wider">Oda</div>
                    <div className="font-display text-[13px] text-[#F4ECDF] font-medium mt-0.5">{p.rooms}</div>
                  </div>
                  <div>
                    <div className="font-body text-[9px] text-white/40 uppercase tracking-wider">AI</div>
                    <div className="font-display text-[13px] text-[#68E5B2] font-medium mt-0.5">{p.aiScore}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom Quick Modules Bar — desktop only (mobilde gizli, mobil topbar var) */}
      <section
        className="hidden lg:block fixed bottom-6 left-[110px] right-[250px] rounded-3xl p-4 z-30"
        style={{
          background: 'linear-gradient(135deg, rgba(15,8,4,0.92), rgba(28,19,13,0.88))',
          backdropFilter: 'blur(24px) saturate(140%)',
          border: '1px solid rgba(197, 108, 57, 0.40)',
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.7), 0 1px 0 rgba(244,213,184,0.10) inset',
        }}
        data-testid="quick-modules-bar"
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 px-3">
            <div className="font-body text-[9px] tracking-[0.28em] text-[#E08A4F] font-semibold uppercase mb-1">
              Hızlı Modüller
            </div>
            <div className="font-display text-[11px] text-[#F4ECDF]/60 tracking-tight">
              Tek panelden tüm sürece erişim
            </div>
          </div>

          <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#C56C39]/40 to-transparent flex-shrink-0" />

          <div className="flex-1 grid grid-cols-5 gap-2">
            {quickModules.map(m => {
              const Icon = m.icon;
              return (
                <button
                  key={m.label}
                  className="rounded-2xl px-4 py-3 flex items-center gap-3 transition-all hover:scale-[1.02] group"
                  style={{
                    background: 'linear-gradient(135deg, rgba(244,236,223,0.04), rgba(244,236,223,0.01))',
                    border: '1px solid rgba(197,108,57,0.20)',
                  }}
                  data-testid={`quick-${m.label.toLowerCase()}`}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, rgba(224,138,79,0.20), rgba(197,108,57,0.10))',
                      border: '1px solid rgba(197,108,57,0.30)',
                    }}
                  >
                    <Icon size={15} className="text-[#E08A4F]" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="font-display text-[12px] text-[#F4ECDF] font-medium tracking-tight">{m.label}</div>
                    <div className="font-body text-[10px] text-[#F4ECDF]/45 truncate">{m.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCell({ value, label, delta, dark = false }) {
  return (
    <div>
      <div className={`font-body text-[10px] tracking-[0.22em] uppercase mb-2 font-semibold ${dark ? 'text-[#E08A4F]/85' : 'text-[#5A2F12]/70'}`}>{label}</div>
      <div className="flex items-baseline gap-2">
        <div className={`font-display text-[30px] font-semibold tracking-tight ${dark ? 'text-[#F4ECDF]' : 'text-[#1C130D]'}`}>{value}</div>
        <div className="font-body text-[11px] text-[#68E5B2] font-semibold">{delta}</div>
      </div>
    </div>
  );
}


/* ============ MOBILE TOP BAR (sm:hidden) ============ */
function MobileTopBar({ navItems, activeNav, setActiveNav, onBack }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className="sm:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3"
        style={{
          background: 'linear-gradient(180deg, #2B1E16 0%, #1C130D 100%)',
          borderBottom: '1px solid rgba(197, 108, 57, 0.40)',
          boxShadow: '0 4px 16px -4px rgba(0,0,0,0.55)',
        }}
        data-testid="mobile-topbar"
      >
        <button
          onClick={onBack}
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(244,213,184,0.06)', border: '1px solid rgba(197,108,57,0.30)' }}
          data-testid="mobile-back"
        >
          <ArrowLeft size={16} className="text-[#E08A4F]" />
        </button>
        <div className="font-display copper-shine font-bold tracking-[0.32em] text-[14px] uppercase pl-[0.32em]">
          {activeNav}
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(244,213,184,0.06)', border: '1px solid rgba(197,108,57,0.30)' }}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={16} className="text-[#E08A4F]" /> : <Menu size={16} className="text-[#E08A4F]" />}
        </button>
      </div>

      {open && (
        <div
          className="sm:hidden fixed inset-0 z-40 pt-14"
          style={{ background: 'rgba(11,7,3,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-overlay"
        >
          <div
            className="mx-4 mt-4 rounded-2xl p-3"
            style={{
              background: 'linear-gradient(180deg, #2B1E16 0%, #1C130D 100%)',
              border: '1px solid rgba(197,108,57,0.40)',
              boxShadow: '0 20px 40px -10px rgba(0,0,0,0.6)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-2 gap-2">
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = item.label === activeNav;
                return (
                  <button
                    key={item.key}
                    onClick={() => { setActiveNav(item.label); setOpen(false); }}
                    className="flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all"
                    style={{
                      background: isActive ? 'linear-gradient(135deg, #E08A4F, #C56C39)' : 'rgba(244,213,184,0.04)',
                      border: isActive ? '1px solid rgba(244,213,184,0.45)' : '1px solid rgba(197,108,57,0.18)',
                    }}
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    <Icon size={15} className={isActive ? 'text-[#1C130D]' : 'text-[#E08A4F]'} />
                    <span className={`font-display text-[12px] ${isActive ? 'text-[#1C130D] font-semibold' : 'text-[#F4ECDF]'} tracking-tight`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
